import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { useState } from 'react';
import { Formik } from 'formik';

import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs, CardTitle } from 'src/core/ui/ContentCard';
import { FormField } from 'src/core/forms/FormField';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import DetailPanelContainer from 'src/core/tables/helpers/DetailPanelContainer';

import FormHelper from 'src/core/forms/formHelper';

import { useInvoiceForm } from 'src/core/forms/useForms';
import { useInvoiceRowDetailsTable, useInvoiceRowsTable } from 'src/pages/orders/payments/invoices/useInvoicesTable';

import PrintInvoiceButton from 'src/shared/printing/PrintInvoiceButton';

export const InvoiceFormPage = () => {
  const [editId, pageTitle] = FormHelper.getEditPageConfig('Invoice');
  const initialValues = useInvoiceForm(editId, defaultInitialValues);
  const [rowsColumns, rowsTableSettings] = useInvoiceRowsTable(initialValues);
  const tabs: CardTab[] = [
    { value: 'Invoice Rows', label: 'Invoice Rows' },
    { value: 'Shipping Address', label: 'Shipping Address' }
  ];
  const [currentTab, setCurrentTab] = useState<string>('Invoice Rows');

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={() => {}} enableReinitialize>
        {(props): JSX.Element => (
          <div>
            <PageHeader title={pageTitle} initialValues={initialValues} subTitle={'View and Print Invoice'} components={[{ isInGroupedMenu: false, component: <PrintInvoiceButton invoiceId={editId} /> }]}
            />
            <ContentCardsContainer>
              <ContentCard {...{ cardId: 'Invoice Info', md: 6, formLayout: true, cardHeader: <CardTitle icon={<ReceiptLongOutlinedIcon />}> Invoice Info</CardTitle> }}>
                <FormField fieldProps={{ name: 'SalesOrderId', label: 'Sales Order Id', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'CustomerAccountUserId', label: 'Customer Account User Id', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'SalesOrganizationId', label: 'Sales Organization Id', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'CustomerReference', label: 'Customer Reference', md: 6, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'CustomerOrderReference', label: 'Customer Order Reference', md: 6, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'ShippingMethod', label: 'Shipping Method Key', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'ShippingMethod_Value', label: 'Shipping Method', md: 8, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'FulfillDate', label: 'Fulfill Date', type: 'date', md: 6, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'TaxIncluded', checkbox: true, label: 'Tax Included', md: 6, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'Specification', label: 'Specification', multiLine: true, rows: 3, md: 12, disabled: true }} formikProps={props} />
              </ContentCard>

              <ContentCard {...{ cardId: 'Totals', md: 6, formLayout: true, cardHeader: <CardTitle icon={<PaymentsOutlinedIcon />}> Totals</CardTitle> }}>
                <FormField fieldProps={{ name: 'TotalAmountNoG', label: 'Total Amount (NoG)', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'TotalAmountNet', label: 'Total Amount (Net)', md: 4, disabled: true }} formikProps={props} />
                <FormField fieldProps={{ name: 'TotalAmountGross', label: 'Total Amount (Gross)', md: 4, disabled: true }} formikProps={props} />
              </ContentCard>
            </ContentCardsContainer>
            <ContentCardsContainer>
              <ContentCard {...{ cardId: 'TabContainer', contentPadding: 0, cardTypes: [CardType.tabContainer], cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} /> }} >
                {currentTab === 'Invoice Rows' && (
                  <ContentCard {...{ cardId: 'Invoice Rows', cardTypes: [CardType.tab], contentPadding: 0 }}>
                    <CustomDataGrid columns={rowsColumns} tableSettings={rowsTableSettings} getDetailPanelContent={(row) => <DetailPanelContent {...{ row }} />} />
                  </ContentCard>
                )}
                {currentTab === 'Shipping Address' && (
                  <ContentCard {...{ cardId: 'Shipping Address', cardTypes: [CardType.tab], formLayout: true, cardHeader: <CardTitle icon={<LocalShippingOutlinedIcon />}> Shipping Address</CardTitle> }} >
                    <FormField fieldProps={{ name: 'ShippingAddress_Company', label: 'Company', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_Department', label: 'Department', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_FirstName', label: 'First Name', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_MiddleNameInitial', label: 'Middle Initial', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_LastName', label: 'Last Name', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_Street', label: 'Street', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_StreetAdditionalInformation', label: 'Street Additional Information', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_Postcode', label: 'Postcode', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_City', label: 'City', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_County', label: 'County', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_State', label: 'State', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_Country.Key', label: 'Country Key', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_Country.Value', label: 'Country', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_EmailAddress', label: 'Email Address', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_PhoneNumber', label: 'Phone Number', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_MobileNumber', label: 'Mobile Number', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_FaxNumber', label: 'Fax Number', md: 6, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_DoorCode', label: 'Door Code', md: 4, disabled: true }} formikProps={props} />
                    <FormField fieldProps={{ name: 'ShippingAddress_AdditionalInformation', label: 'Additional Information', md: 8, disabled: true }} formikProps={props} />
                  </ContentCard>
                )}
              </ContentCard>
            </ContentCardsContainer>
          </div>
        )}
      </Formik>
      <Footer />
    </>
  );
};

export default InvoiceFormPage;
const DetailPanelContent = ({ row }) => {
  const [columns, tableSettings] = useInvoiceRowDetailsTable(row?.row?.InvoiceRowDetails);
  return (
    <DetailPanelContainer depth={0}>
      <CustomDataGrid columns={columns} tableSettings={tableSettings} />
    </DetailPanelContainer>
  );
};

const defaultInitialValues = {};
